import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, todayISO, formatDateTime } from "../lib/api";
import { toast } from "sonner";
import { Save, ScaleIcon, AlertTriangle, CheckCircle2, Zap, History } from "lucide-react";

export default function Tally() {
  const [godowns, setGodowns] = useState([]);
  const [date, setDate] = useState(todayISO());
  const [godown, setGodown] = useState("");
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);

  const loadGodowns = async () => {
    const { data } = await api.get("/godowns");
    setGodowns(data);
    if (!godown && data.length) setGodown(data[0].name);
  };

  const loadSuggest = async () => {
    if (!godown || !date) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tally/suggest?date=${date}&godown=${encodeURIComponent(godown)}`);
      setData(data);
      setNote(data.existing_note || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadHistory = async () => {
    const { data } = await api.get("/tally?limit=30");
    setHistory(data.slice(0, 10));
  };
  const loadPending = async () => {
    const { data } = await api.get("/tally/pending-dates");
    setPending(data.pending || []);
  };

  useEffect(() => { loadGodowns(); loadHistory(); loadPending(); }, []);
  useEffect(() => { loadSuggest(); /* eslint-disable-next-line */ }, [date, godown]);

  const updateRow = (i, field, val) => {
    const next = { ...data, rows: [...data.rows] };
    next.rows[i] = { ...next.rows[i], [field]: Number(val) };
    setData(next);
  };

  const save = async () => {
    if (!data) return;
    setBusy(true);
    try {
      const payload = {
        date, godown,
        note,
        rows: data.rows.map((r) => ({
          brand: r.brand, size: r.size,
          ground_filled: Number(r.ground_filled || 0),
          ground_mt: Number(r.ground_mt || 0),
        })),
      };
      const { data: saved } = await api.post("/tally", payload);
      toast.success(saved.has_mismatch
        ? `Tally saved with mismatch (${saved.total_filled_diff} Filled, ${saved.total_mt_diff} MT diff)`
        : "Tally saved — stock matches perfectly");
      loadSuggest(); loadHistory(); loadPending();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const applyAdjustment = async (tid) => {
    if (!window.confirm("Create an adjustment transaction to absorb the mismatch?")) return;
    try {
      const { data } = await api.post(`/tally/${tid}/apply-adjustment`);
      toast.success(data.adjustment_id ? `Adjustment created for ${data.items_count} line(s)` : data.message);
      loadHistory(); loadPending();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mismatches = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      const df = (Number(r.ground_filled || 0) - r.system_filled);
      const dm = (Number(r.ground_mt || 0) - r.system_mt);
      return df !== 0 || dm !== 0;
    });
  }, [data]);

  return (
    <div className="space-y-6" data-testid="tally-page">
      {pending.length > 0 && (
        <div className="card-flat p-4 bg-[#FEE2E2] border-[#FECACA]" data-testid="tally-pending-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-[#B91C1C] mt-0.5" strokeWidth={2.5} />
            <div className="flex-1">
              <div className="small-label text-[#B91C1C]">Blocking</div>
              <div className="font-heading font-bold text-lg text-[#B91C1C]">
                Tally pending for {pending.length} day{pending.length === 1 ? "" : "s"}
              </div>
              <div className="text-sm text-gray-700 mt-1">
                New entries are blocked until each day below is tallied for both godowns.
              </div>
              <ul className="mt-2 text-sm">
                {pending.map((p) => (
                  <li key={p.date} className="font-mono">
                    {p.date} — missing: <span className="font-bold">{p.missing_godowns.join(", ")}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="card-flat p-5 flex items-center gap-4 flex-wrap">
        <ScaleIcon size={24} className="text-gray-500" />
        <div className="flex-1 min-w-[200px]">
          <div className="small-label text-gray-500">Date</div>
          <input type="date" className="input-lg max-w-[200px]" value={date}
            onChange={(e) => setDate(e.target.value)} data-testid="tally-date" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="small-label text-gray-500">Godown</div>
          <select className="input-lg" value={godown}
            onChange={(e) => setGodown(e.target.value)} data-testid="tally-godown">
            {godowns.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
          </select>
        </div>
        {data?.existing && (
          <div className="small-label text-[#15803D] flex items-center gap-1">
            <CheckCircle2 size={14} /> Already tallied — re-saving will overwrite
          </div>
        )}
      </div>

      {/* Late/backdated entries banner — explains why a previously-matched tally
          now shows mismatch when re-opened */}
      {data?.existing && data?.late_entries?.length > 0 && (
        <div className="card-flat p-4 bg-[#FEF3C7] border-[#FDE68A]" data-testid="late-entries-banner">
          <div className="flex items-start gap-3">
            <History size={20} className="text-[#B45309] mt-0.5" strokeWidth={2.5} />
            <div className="flex-1 min-w-0">
              <div className="small-label text-[#B45309]">Why the numbers changed</div>
              <div className="font-heading font-bold text-base">
                {data.late_entries.length} entr{data.late_entries.length === 1 ? "y" : "ies"} dated ≤ {date} {data.late_entries.length === 1 ? "was" : "were"} added after this tally was saved
                {data.existing_saved_at ? ` (saved ${formatDateTime(data.existing_saved_at)})` : ""}
              </div>
              <div className="text-xs text-gray-700 mt-1 mb-2">
                System stock is always recalculated live — these late entries are why the system numbers differ from what you tallied in the morning.
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-gray-500">
                      <th className="pr-3 py-1">Entry date</th>
                      <th className="pr-3 py-1">Type</th>
                      <th className="pr-3 py-1">Challan</th>
                      <th className="pr-3 py-1">Stock change</th>
                      <th className="pr-3 py-1">Entered</th>
                      <th className="py-1">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.late_entries.map((e, i) => (
                      <tr key={i} className="border-t border-[#FDE68A]" data-testid={`late-entry-${i}`}>
                        <td className="pr-3 py-1 data-num">{e.date}</td>
                        <td className="pr-3 py-1 capitalize">{e.type}</td>
                        <td className="pr-3 py-1">{e.challan_no || "—"}</td>
                        <td className="pr-3 py-1 font-semibold">{e.changes}</td>
                        <td className="pr-3 py-1 data-num">{formatDateTime(e.created_at)}</td>
                        <td className="py-1">{e.created_by_email || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="small-label text-gray-500">System vs Ground count</div>
            <div className="font-heading font-bold text-xl">
              {godown} · {date}
            </div>
          </div>
          {mismatches.length > 0 && (
            <div className="flex items-center gap-2 text-[#B45309] bg-[#FEF3C7] px-3 py-1.5 rounded-sm border border-[#FDE68A] text-sm">
              <AlertTriangle size={14} />
              <span className="font-bold">{mismatches.length} mismatch(es)</span>
            </div>
          )}
        </div>
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Brand</th>
                <th className="text-left">Size</th>
                <th className="num">System Filled</th>
                <th className="num">Ground Filled</th>
                <th className="num">Diff</th>
                <th className="num">System MT</th>
                <th className="num">Ground MT</th>
                <th className="num">Diff</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan="8" className="py-6 text-center text-gray-400">Loading…</td></tr>}
              {!loading && (!data?.rows || data.rows.length === 0) && (
                <tr><td colSpan="8" className="py-10 text-center text-gray-400 text-sm">
                  Nothing to tally — no stock on this date/godown.
                </td></tr>
              )}
              {!loading && data?.rows?.map((r, i) => {
                const df = Number(r.ground_filled || 0) - r.system_filled;
                const dm = Number(r.ground_mt || 0) - r.system_mt;
                return (
                  <tr key={`${r.brand}-${r.size}`} className={r.not_in_master ? "bg-[#FEF3C7]/40" : ""}>
                    <td className="font-semibold">
                      {r.brand}
                      {r.not_in_master && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[9px] rounded-sm bg-[#B45309] text-white font-bold" title="This brand/size exists in old entries but is missing from Masters — add it there to keep tally clean">NOT IN MASTER</span>
                      )}
                    </td>
                    <td>{r.size}</td>
                    <td className="num data-num">
                      {r.system_filled}
                      {r.saved_system_filled != null && r.saved_system_filled !== r.system_filled && (
                        <div className="text-[10px] text-[#B45309] font-semibold" title="System stock frozen when this tally was saved">was {r.saved_system_filled} at tally</div>
                      )}
                    </td>
                    <td>
                      <input type="number" className="input-lg h-9 text-right data-num"
                        value={r.ground_filled}
                        onChange={(e) => updateRow(i, "ground_filled", e.target.value)}
                        data-testid={`tally-gf-${i}`} />
                    </td>
                    <td className={`num font-bold ${df === 0 ? "text-gray-400" : df > 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                      {df > 0 ? "+" : ""}{df || "·"}
                    </td>
                    <td className="num data-num">
                      {r.system_mt}
                      {r.saved_system_mt != null && r.saved_system_mt !== r.system_mt && (
                        <div className="text-[10px] text-[#B45309] font-semibold" title="System stock frozen when this tally was saved">was {r.saved_system_mt} at tally</div>
                      )}
                    </td>
                    <td>
                      <input type="number" className="input-lg h-9 text-right data-num"
                        value={r.ground_mt}
                        onChange={(e) => updateRow(i, "ground_mt", e.target.value)}
                        data-testid={`tally-gm-${i}`} />
                    </td>
                    <td className={`num font-bold ${dm === 0 ? "text-gray-400" : dm > 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                      {dm > 0 ? "+" : ""}{dm || "·"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-200 flex items-center gap-3 flex-wrap">
          <input className="input-lg flex-1 min-w-[240px]" placeholder="Note (optional)"
            value={note} onChange={(e) => setNote(e.target.value)} data-testid="tally-note" />
          <button onClick={save} disabled={busy || !data?.rows?.length}
            className="btn-primary h-11 flex items-center gap-2" data-testid="tally-save">
            <Save size={16} /> {busy ? "Saving…" : "Save Tally"}
          </button>
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 small-label text-gray-500">Recent tallies</div>
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Godown</th>
                <th className="num">Filled Δ</th>
                <th className="num">MT Δ</th>
                <th className="text-left">Status</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id}>
                  <td className="data-num text-xs">{h.date}</td>
                  <td className="font-semibold">{h.godown}</td>
                  <td className={`num font-bold ${h.total_filled_diff === 0 ? "text-gray-500" : "text-[#B91C1C]"}`}>
                    {h.total_filled_diff > 0 ? "+" : ""}{h.total_filled_diff}
                  </td>
                  <td className={`num font-bold ${h.total_mt_diff === 0 ? "text-gray-500" : "text-[#B91C1C]"}`}>
                    {h.total_mt_diff > 0 ? "+" : ""}{h.total_mt_diff}
                  </td>
                  <td>
                    {h.has_mismatch ? (
                      <span className="stock-mt px-2 py-0.5 text-xs rounded-sm">mismatch</span>
                    ) : (
                      <span className="stock-filled px-2 py-0.5 text-xs rounded-sm">matched</span>
                    )}
                  </td>
                  <td className="text-right">
                    {h.has_mismatch && !h.adjustment_txn_id && (
                      <button onClick={() => applyAdjustment(h.id)} className="text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                        <Zap size={14} /> Apply adjustment
                      </button>
                    )}
                    {h.adjustment_txn_id && (
                      <span className="small-label text-[#15803D]">adjusted</span>
                    )}
                  </td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan="6" className="py-8 text-center text-gray-400 text-sm">No tallies yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
